import { Suspense, lazy, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { SessionUser } from "./details-page";
import { splitCourse } from "../lib/menu-course";
import { blockFaqAnswer } from "../lib/block-faq-answer";
import { BookingSkinScope, useBookingSkinFor } from "../skin";
import { useBookingTrack } from "../provider";
import { activityTrackProps } from "../lib/track-props";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { isTripFull, type WomenTripInfo } from "../lib/women-trip";
import { WomenTripLine } from "../components/women-trip-parts";
import { Container } from "../internal-ui/container";
import { useActivity } from "../hooks/use-activity";
import { ActivityPageTop, splitTopFacts, topPaymentKind } from "../components/activity-page-top";
import { isRestaurantListing, parseOpeningHours, type OpeningHours, type TablePolicy } from "@majourneys/shared";
import { openingHoursToSchemaOrg } from "../lib/opening-hours-schema-org";
import { FactTiles } from "../components/restaurant/fact-tiles";
import { WhyGoPanel, readWhyGo } from "../components/restaurant/why-go-panel";
import { LocationAndHours } from "../components/restaurant/location-and-hours";
import { OpenNowPill } from "../components/restaurant/open-now-pill";
import {
  ActivitySidebar,
  type ActivitySidebarProps,
  type BookingSelection,
} from "../components/activity-sidebar";
import { BookingBar } from "../components/booking-bar";
import { useDisplayPriceParts } from "../lib/currency";
import { ShareModal } from "../components/share-modal";
import { TransferForm } from "../components/transfer-form";
import {
  ACTIVITY_IMAGE_PLACEHOLDER,
  OG_LOCALE,
  SUPPORTED_LANGS,
  isNightclubBooking,
  moroccoStartOfToday,
  parseLang,
  paysByLink,
  showsSubtitle,
  showsWhyGoPanel,
  WHATSAPP_URL,
  type TransferRouteConfig,
} from "@majourneys/shared";
import { PW, pewterPanel } from "../internal-ui/pewter-shell";
// Eager (not lazy): lazy components skip SSR entirely here, and this section
// carries the page's only lateral venue links — crawlers must see them. The
// route loader prefetches its query; without SSR the links never reach the
// HTML (verified with curl 2026-07-11).
import { SimilarListings } from "../components/similar-listings";
import { useBookingUnitLabels } from "../lib/booking-unit";
import { carriedPartyQuery, withCarriedQuery } from "../lib/sidebar-party";
import { formatMoroccoLocation, getMoroccoName } from "../lib/locale";
import { activityHrefString } from "../lib/activity-href";

import { Icon } from "@majourneys/icons";
import { toLocalISO } from "../lib/local-date";
import { Toaster } from "../internal-ui/toast";
import { LocationCard } from "../components/location-card";
/**
 * Shape of the activity detail as returned by the API (Eden treaty response).
 * This is intentionally broader than @majourneys/shared ActivityDetail — the
 * API layer adds explore-mode fields (mode, segment, faqs, contentBlocks, etc.)
 * that the old schema type doesn't carry.
 */
export interface ShowActivity {
  id: string;
  slug: string;
  title: string;
  subtitle?: string | null;
  overview: string;
  cityName: string;
  city: { id: string; name: string; slug: string };
  category: { id: string; name: string; slug: string } | null;
  images: string[];
  price: number;
  typePrice: number;
  rating?: number | string | null;
  reviewsCount?: number | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  reservePayLater?: boolean;
  freeCancellation?: boolean;
  whatsappConfirmation?: boolean;
  template?: string | null;
  transfer?: TransferRouteConfig | null;
  contentBlocks?: Record<string, unknown> | null;
  menuLink?: string | null;
  faqs?: Array<{ id: string; question: string; answer: string }> | null;
  /** Short fact pills, already localized by the API. */
  facts?: readonly string[] | null;
  /** Weekly doors-open hours (display only). Null when unknown. */
  openingHours?: OpeningHours;
  /** The venue keeps its exact location private until booking. */
  hideLocation?: boolean;
  packages: Array<{
    id: string;
    name: string;
    description: string;
    timeStart?: string | null;
    timeEnd?: string | null;
    maxGuests: number;
    isMostPopular: boolean;
    typePrice?: number;
    pricing: { adult: number; child: number; infant: number; transport: number };
  }>;
  mode?: "bookable" | "explore";
  segment?: string | null;
  /** Per-activity guest age bounds. When both set, the booking UI shows derived
   * age labels (infants "Under N", children "M–N", adults "N+ years"). */
  infantMaxAge?: number | null;
  childrenMaxAge?: number | null;
  /** Smallest party the activity takes (adults + children). 1 = no minimum. */
  minPeople?: number | null;
  /** Fixed daily session times ("HH:mm") for appointment-based venues; empty
   * means the hourly grid from /times applies. */
  sessionTimes?: readonly string[] | null;
  /** Weekdays bookings are taken on; null means every day. */
  openDays?: readonly string[] | null;
  minNoticeHours?: number | null;
  /** Restaurant house rules the table form warns about. Null when none. */
  tablePolicy?: TablePolicy | null;
  /** Taken off sale without being unpublished. */
  bookingPaused?: boolean | null;
  bookingResumesOn?: string | null;
  bookingPausedNote?: string | null;
  /** A women-only group trip; null for every other activity. */
  womenTrip?: WomenTripInfo | null;
}

// Below-the-fold + on-demand. Lazy-load so the initial render path doesn't
// pay for booking flow + info-tabs + FAQ + similar-listings JS before LCP fires.
const ActivityOptions = lazy(() =>
  import("../components/activity-options").then((m) => ({ default: m.ActivityOptions })),
);
const GuideItinerary = lazy(() =>
  import("../components/guide-itinerary").then((m) => ({ default: m.GuideItinerary })),
);
const ActivityFaq = lazy(() =>
  import("../components/activity-faq").then((m) => ({ default: m.ActivityFaq })),
);
const NightclubSheet = lazy(() =>
  import("../components/nightclub-sheet").then((m) => ({ default: m.NightclubSheet })),
);
const NightclubSidebar = lazy(() =>
  import("../components/nightclub-sidebar").then((m) => ({ default: m.NightclubSidebar })),
);
const NightclubFloorPlan = lazy(() =>
  import("../components/nightclub-floorplan").then((m) => ({ default: m.NightclubFloorPlan })),
);
const ActivityLineup = lazy(() =>
  import("../components/activity-lineup").then((m) => ({ default: m.ActivityLineup })),
);
const ActivityMenu = lazy(() =>
  import("../components/activity-menu").then((m) => ({ default: m.ActivityMenu })),
);
const ActivityInfoTabs = lazy(() =>
  import("../components/activity-info-tabs").then((m) => ({ default: m.ActivityInfoTabs })),
);
const ActivityFloorPlan = lazy(() =>
  import("../components/activity-floor-plan").then((m) => ({ default: m.ActivityFloorPlan })),
);
const VillaPolicySection = lazy(() =>
  import("../components/villa-policy-section").then((m) => ({ default: m.VillaPolicySection })),
);
const AvailablePacks = lazy(() =>
  import("../components/available-packs").then((m) => ({ default: m.AvailablePacks })),
);
const BookingSheet = lazy(() =>
  import("../components/booking-sheet").then((m) => ({ default: m.BookingSheet })),
);

/* The structured hours card replaces the editorial free-text hours block. */
const HIDDEN_WHEN_HOURS_KNOWN = ["openingHours"] as const;
const HIDDEN_WHEN_WHY_GO = ["highlights"] as const;

const PRICE_LABEL_KEYS: Record<number, string> = {
  0: "activity.person",
  1: "activity.hour",
  2: "activity.night",
  3: "activity.group",
};

export interface ActivityShowPageProps {
  /**
   * The venue's published reel, looked up by the host app (the website reads
   * GET /reels by activitySlug, as the app does). Adds "Watch video" on the
   * photos; omit and the photos show alone.
   */
  video?: { url: string; poster?: string } | null;
  /**
   * When omitted (city sites), wishlist heart is hidden entirely.
   * When true, the heart icon is shown.
   */
  isSignedIn?: boolean;
  /** The signed-in guest, so the booking sheet fills in their name and email. */
  sessionUser?: SessionUser;
  /**
   * Set of activity slugs the signed-in user has saved.
   * Used to initialise the wishlisted state. Only used when isSignedIn=true.
   */
  savedActivitySlugs?: Set<string>;
  /**
   * Called when user taps the heart button (sign-in required).
   * Receives the activity slug and the new desired wishlisted state.
   */
  onToggleWishlist?: (slug: string, wishlisted: boolean) => void;
  /**
   * Called when a signed-out guest taps the heart. The host opens its sign-in
   * with the item on top and saves it once they are in. Without it the heart
   * stays hidden for signed-out guests, as before (city sites).
   */
  onSaveNeedsSignIn?: (item: { slug: string; title: string; image?: string }) => void;
  /**
   * Called on mount to record recently-viewed (bookable items only).
   * No-op when undefined (city sites).
   */
  onRecentViewed?: (item: {
    slug: string;
    title: string;
    city: string;
    price: number;
    currency: string;
    priceType?: number;
    image?: string;
    category?: string;
  }) => void;
  /**
   * Render slot for SEO head tags (title, canonical, JSON-LD).
   * City sites pass a full <PageSeo> + JSON-LD tree here.
   * majourneys passes nothing (uses its built-in head tags).
   */
  renderSeo?: (input: { activity: ShowActivity; lang: string }) => ReactNode;
  /**
   * When provided, overrides the canonical URL used in head tags.
   * City sites pass their own origin-based canonical.
   */
  canonicalUrl?: string;
  /**
   * Render an extra inline content block below the gallery
   * (e.g. neighbourhood + local tips for city explore wrappers).
   */
  renderInlineSlot?: (input: { activity: ShowActivity; lang: string }) => ReactNode;
  /**
   * SSR fallback rows for the Similar-listings section (route-loader
   * prefetch). Without it the section's lateral venue links never reach
   * crawler-visible HTML.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  similarItems?: any[];
  /**
   * SSR fallback rows for the same-city widening, used when the activity is
   * the only one in its category in that city.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  similarCityItems?: any[];
  /**
   * Render a section below the "similar listings" strip — used by majourneys
   * to surface "Read about {city}" blog articles (a reverse link from the
   * activity page into editorial content). Omitted by city sites, which have
   * no blog surface, so nothing renders there.
   */
  renderRelatedArticles?: (input: { activity: ShowActivity; lang: string }) => ReactNode;
}

/**
 * The booking form's answers from the address, for a link that opens it
 * filled in: MaJourneys AI's proposal card sends
 * `?book=1&date=2026-10-05&time=21:30&adults=2`. Anything malformed is
 * dropped, so a bad link opens an empty form rather than a wrong one.
 */
export function selectionFromSearch(search: {
  date?: unknown;
  time?: unknown;
  adults?: unknown;
  children?: unknown;
  checkOut?: unknown;
  women?: unknown;
  men?: unknown;
}): BookingSelection | undefined {
  const isoDay = (v: unknown) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined);
  const date = isoDay(search.date);
  const checkOut = isoDay(search.checkOut);
  const time = typeof search.time === "string" && /^\d{2}:\d{2}$/.test(search.time) ? search.time : undefined;
  const count = (v: unknown, max: number) => {
    const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
    return Number.isInteger(n) && n >= 0 && n <= max ? n : undefined;
  };
  const adults = count(search.adults, 50);
  const children = count(search.children, 20);
  const women = count(search.women, 50);
  const men = count(search.men, 50);
  if (!date && !time && !adults) return undefined;
  return {
    ...(date ? { date } : {}),
    ...(checkOut && date && checkOut > date ? { checkOut } : {}),
    ...(women != null && men != null ? { women, men } : {}),
    ...(time ? { time } : {}),
    ...(adults ? { adults } : {}),
    ...(children != null ? { children } : {}),
  };
}

function ShowContent(props: ActivityShowPageProps) {
  const { i18n, t } = useTranslation();
  const { lang, slug } = useParams({ strict: false }) as { lang: string; slug: string };
  const skin = useBookingSkinFor(slug);
  const search = useSearch({ strict: false }) as {
    book?: unknown;
    date?: unknown;
    time?: unknown;
    adults?: unknown;
    children?: unknown;
    checkOut?: unknown;
    women?: unknown;
    men?: unknown;
    pkg?: unknown;
  };
  const navigate = useNavigate();
  const { data: response } = useActivity(slug);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = response.data as any as ShowActivity;
  const a = useMemo(() => withTransferFallbackImage(raw), [raw]);
  const unitLabels = useBookingUnitLabels(a.category?.slug ?? null, slug);
  const displayPrice = useDisplayPriceParts();
  const track = useBookingTrack();
  useEffect(() => {
    track("activity_viewed", {
      ...activityTrackProps(a),
      mode: a.mode ?? "bookable",
      price_from: a.price,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a.slug]);
  const galleryRef = useRef<HTMLDivElement>(null);
  const wantsBook =
    search.book === 1 || search.book === "1" || search.book === true || search.book === "true";
  const [bookingSheetOpen, setBookingSheetOpen] = useState(wantsBook);
  const [initialPkgId, setInitialPkgId] = useState<string | undefined>(() =>
    wantsBook && typeof search.pkg === "string" && search.pkg ? search.pkg : undefined,
  );
  // What the guest picked in the sidebar / booking bar before opening the
  // sheet, so the sheet continues that selection rather than its own defaults.
  const [initialSelection, setInitialSelection] = useState<BookingSelection | undefined>(() =>
    wantsBook ? selectionFromSearch(search) : undefined,
  );
  // The side panel starts from the same address, book=1 or not, so it quotes
  // the party the guest arrived with (read once: the panel owns it after).
  const [urlSelection] = useState<BookingSelection | undefined>(() => selectionFromSearch(search));
  const [bucketToast, setBucketToast] = useState<{ count: number } | null>(null);

  // Wishlist state — driven by DI props (no auth import in this package).
  const [wishlisted, setWishlisted] = useState(
    props.isSignedIn && props.savedActivitySlugs ? props.savedActivitySlugs.has(a.slug) : false,
  );
  const showHeart =
    !!props.onToggleWishlist && (!!props.isSignedIn || !!props.onSaveNeedsSignIn);
  // One handler for the sidebar heart and Save at the top of the page, so the
  // two never disagree: signed out it asks to sign in (then saves), signed in
  // it toggles.
  const onHeart = () => {
    if (!props.isSignedIn) {
      props.onSaveNeedsSignIn?.({ slug: a.slug, title: a.title, image: a.images?.[0] });
      return;
    }
    const next = !wishlisted;
    setWishlisted(next);
    props.onToggleWishlist!(a.slug, next);
  };

  // Keep wishlisted in sync if the caller refreshes savedActivitySlugs.
  useEffect(() => {
    if (!props.isSignedIn || !props.savedActivitySlugs) {
      setWishlisted(false);
      return;
    }
    setWishlisted(props.savedActivitySlugs.has(a.slug));
  }, [props.isSignedIn, props.savedActivitySlugs, a.slug]);


  const openBookingSheet = (packageId?: string, selection?: BookingSelection) => {
    setInitialPkgId(packageId);
    setInitialSelection(selection);
    setBookingSheetOpen(true);
  };

  // Auto-dismiss the "added to bucket" toast after ~5s.
  useEffect(() => {
    if (!bucketToast) return;
    const id = setTimeout(() => setBucketToast(null), 5000);
    return () => clearTimeout(id);
  }, [bucketToast]);

  // Auto-open the BookingSheet when arriving with ?book=1 and strip the param.
  // Only the "open the sheet" part goes: the day and party stay in the
  // address, so a refresh, the breadcrumbs and "See all" still carry them.
  useEffect(() => {
    if (!wantsBook) return;
    setBookingSheetOpen(true);
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => {
        const { book: _drop, pkg: _p, ...rest } = prev;
        return rest;
      },
      replace: true,
    });
  }, [wantsBook, navigate]);

  const isExplore = a.mode === "explore";
  // A women-only group trip: fixed dates, seats, a deposit to the organiser.
  const womenTrip = !isExplore ? a.womenTrip ?? null : null;
  const tripFull = womenTrip ? isTripFull(womenTrip, toLocalISO(moroccoStartOfToday())) : false;
  // The organiser's page keeps the query the guest arrived with.
  const searchStr = queryStringOf(search as Record<string, unknown>);
  /* This page is one venue, so its category reads singular: NIGHTCLUB, not
     NIGHTCLUBS (owner, 2026-09-16). The hub at /category/<slug> still lists
     forty of them and keeps the plural, so the singular lives in the locale
     files rather than in the category's own name. A slug with no singular
     entered falls back to that name. */
  const categoryLabel = a.category
    ? t(`category_singular.${a.category.slug}`, { defaultValue: a.category.name })
    : null;
  const crumbs = [
    { label: t("common.home", { defaultValue: "Home" }), href: `/${lang}` },
    { label: a.cityName, href: `/${lang}/place/${a.city.slug}` },
    ...(a.category
      ? [{ label: categoryLabel ?? a.category.name, href: `/${lang}/category/${a.category.slug}` }]
      : []),
    { label: a.title },
  ];
  // The guest's day and party, carried by every link that leaves this page
  // for a list or the packages page. The JSON-LD keeps the bare `crumbs`.
  const partyQuery = carriedPartyQuery(search as Record<string, unknown>);
  const visibleCrumbs = crumbs.map((c, i) => (c.href && i > 0 ? { ...c, href: withCarriedQuery(c.href, partyQuery) } : c));

  const contentBlocks = a.contentBlocks ?? null;
  const template = a.template ?? null;
  const isStayFlow = template === "stay" || a.typePrice === 2;
  // A bookable restaurant reserves a table — date, time, party size — with no
  // package to pick and no price on the page. Explore restaurants stay
  // editorial-only, which is what makes `mode` the on/off switch per venue.
  const isTableFlow = !isExplore && template === "restaurant";
  // The restaurant sections (open-now pill, fact tiles, hours card) apply to
  // every restaurant page, bookable or listed only: the hours are true either way.
  const isRestaurant = template === "restaurant" || template === "explore-restaurant";
  // The Menu button at the top only for a restaurant whose menu section will
  // actually render: dishes in any locale, or its own menu link.
  const hasMenu =
    isRestaurant &&
    (Object.values(((contentBlocks as Record<string, unknown> | null)?.menuHighlights ?? {}) as Record<string, unknown>).some(
      (v) => Array.isArray(v) && v.some((d) => typeof d === "string" && d.trim().length > 0),
    ) ||
      (typeof a.menuLink === "string" && a.menuLink.trim().length > 0));
  // Parsed, not trusted: a malformed stored week reads as unknown, so the
  // pill, the hours card and the hidden free-text block all fall back together.
  const openingHours = isRestaurant ? parseOpeningHours(a.openingHours) : null;
  // Restaurants, activities, spas, pools and trips can carry "why people go"
  // rows. When they do, a restaurant drops its Highlights tab: there the rows
  // are the highlights, said once (Le Tanjia repeated all four). Activities
  // keep Highlights, which hold the practical details the rows leave out.
  const hasWhyGo =
    showsWhyGoPanel(template) && readWhyGo(contentBlocks as Record<string, unknown> | null, lang).length > 0;
  const hiddenBlocks = [
    // No price on a restaurant, bookable or not (owner rule): tables are free.
    ...(isRestaurant ? (["priceRange"] as const) : []),
    ...(openingHours ? HIDDEN_WHEN_HOURS_KNOWN : []),
    ...(hasWhyGo && isRestaurant ? HIDDEN_WHEN_WHY_GO : []),
  ];
  // Nightclubs get their own three-step flow (see NightclubSheet). The category
  // is the answer when it is there; `isNightclubBooking` also catches a club
  // whose category went missing, which otherwise sells its entry by the hour.
  const isNightclub =
    !isExplore &&
    isNightclubBooking({
      categorySlug: a.category?.slug,
      segment: a.segment,
      packages: a.packages,
    });
  // Transfers sell a ride, not a slot: the route, the customer's places and
  // a per-vehicle price in one form (see TransferForm). The template alone
  // decides it, so a route missing its settings still never falls through to
  // the generic sidebar and sheet; it gets the WhatsApp panel instead.
  const isTransfer = !isExplore && template === "transfer";
  // Only vehicles with a real per-vehicle price (a type 3 price row above 0).
  // A package priced some other way, or not at all, would quote the wrong
  // money, so it is not offered.
  const transferPackages = isTransfer
    ? a.packages
        .filter((p) => p.typePrice === 3 && p.pricing.adult > 0)
        .map((p) => ({ id: p.id, name: p.name, maxGuests: p.maxGuests, price: p.pricing.adult }))
    : [];
  const transferConfig = isTransfer && transferPackages.length > 0 ? a.transfer ?? null : null;
  const bookHref = withCarriedQuery(`/${lang}/${a.slug}/${isStayFlow ? "details" : "packages"}`, partyQuery);

  /* The video's full screen: the city and the from-price under the name,
     the subtitle beside it on desktops, and the same way in as the booking
     bar. Explore items take no booking, so they get no price and "See
     details" (back to the page) instead of "Book now". */
  const priceParts = displayPrice(a.price);
  const videoPrice = isExplore
    ? null
    : isTableFlow
      ? t("activity.table_promise_short")
      : a.price > 0
        ? t("activity.from_price", { price: priceParts.value, currency: priceParts.currency })
        : null;
  const videoMeta = [a.cityName, videoPrice].filter(Boolean).join(" · ") || null;
  const videoCta = isExplore
    ? { label: t("activity.see_details"), onClick: () => {} }
    : a.bookingPaused
      ? null
      : {
          label: womenTrip
            ? tripFull
              ? t("women.join_waiting_list")
              : t("women.reserve_seat")
            : t("activity.book_now"),
          onClick: () => {
            if (isTransfer || tripFull) {
              document.getElementById("activity-booking-sidebar")?.scrollIntoView({ behavior: "smooth" });
            } else if (isTableFlow || a.packages.length > 0) {
              openBookingSheet();
            } else {
              void navigate({ href: bookHref });
            }
          },
        };
  // A transfer shows its own FAQs or none. The generic fallback asks how long
  // "the experience" lasts and where it "takes place", which reads wrong for
  // a ride and would ship that wording into the FAQPage markup.
  const faqs = isTransfer
    ? a.faqs ?? []
    : [
        ...buildContentBlockFaqs(a.title, contentBlocks, i18n.language),
        ...(a.faqs && a.faqs.length > 0 ? a.faqs : buildDefaultFaqs(a, t)),
      ];

  const seoSegment = isExplore ? a.segment ?? null : null;
  const canonicalPath = activityHrefString({
    lang,
    citySlug: a.city?.slug ?? null,
    categorySlug: a.category?.slug ?? null,
    segment: seoSegment,
    slug: a.slug,
  });
  const canonical = props.canonicalUrl ?? `https://majourneys.com${canonicalPath}`;
  const ldDocs = buildActivityJsonLd({
    activity: a,
    lang,
    crumbs,
    faqs,
    canonical,
    transferOffers: isTransfer ? transferPackages : null,
  });
  const altPath = (l: string) =>
    activityHrefString({
      lang: l,
      citySlug: a.city?.slug ?? null,
      categorySlug: a.category?.slug ?? null,
      segment: seoSegment,
      slug: a.slug,
    });
  const countryName = getMoroccoName(lang);
  const locationLabel = formatMoroccoLocation(a.cityName, lang);
  // SERP copy: lead with what the page IS (venue + descriptor) and close the
  // description on the pay-on-arrival wedge — the one hook competitors with
  // upfront-payment checkouts cannot copy. Descriptor comes from the
  // localized category name and is dropped when the title already says it
  // ("Quad Biking in Tangier" doesn't need "· Quad").
  const descriptor = (categoryLabel ?? a.category?.name)?.trim() ?? "";
  const titleHasDescriptor =
    !descriptor || a.title.toLowerCase().includes(descriptor.toLowerCase());
  const parsedLang = parseLang(lang);
  const titleHasCity = a.cityName
    ? a.title.toLowerCase().includes(a.cityName.toLowerCase())
    : true;
  // "Name - City" (owner, 2026-09-25): the hyphen keeps the venue name and the
  // city apart, so "Café Alysse Agadir" does not read as one name.
  const entityName = titleHasCity ? a.title : `${a.title} - ${a.cityName}`;
  const exploreKey = exploreHookKey(a.segment);
  // An explore restaurant or spa cannot be booked through us, so it must not
  // promise "Book a Table" or "pay at the restaurant" (owner, 2026-09-25).
  const exploreHooks = isExplore
    ? (exploreKey === "restaurants" || exploreKey === "spas"
        ? EXPLORE_LISTING_HOOKS[exploreKey][parsedLang]
        : EXPLORE_SEO_HOOKS[exploreKey]?.[parsedLang])
    : undefined;
  // Bookable restaurants and spas read "Name - City: Book a Table"
  // (owner, 2026-09-15): the action is the click reason. The colon keeps the
  // venue name clear of the CTA. Their descriptions keep the pay-on-arrival
  // close.
  const bookableTitleHookKey: ExploreHookKey | null = isExplore
    ? null
    : isTableFlow
    ? "restaurants"
    : a.category?.slug === "spas"
    ? "spas"
    : null;
  const titleHookKey = isExplore ? exploreHookKey(a.segment) : bookableTitleHookKey;
  const titleHooks = exploreHooks ?? (bookableTitleHookKey ? BOOKABLE_SEO_HOOKS[bookableTitleHookKey][parsedLang] : undefined);
  const pageShowsTitleExtra =
    titleHookKey === "restaurants"
      ? hasBlockList(contentBlocks, "menuHighlights")
      : titleHookKey === "spas"
      ? (typeof a.price === "number" && a.price > 0) || hasBlockList(contentBlocks, "priceRange")
      : true;
  const titleHook = pageShowsTitleExtra ? titleHooks?.title : titleHooks?.titleShort ?? titleHooks?.title;
  const metaTitle = titleHook
    ? `${entityName}${titleHook} | MaJourneys`
    : titleHasDescriptor
    ? `${a.title} - ${locationLabel} | MaJourneys`
    : `${a.title} · ${descriptor} - ${locationLabel} | MaJourneys`;
  const payHook = womenTrip
    ? t("women.seo_pay_hook")
    : isStayFlow || paysByLink(a.slug)
      ? STAY_PAID_ONLINE_HOOK[parsedLang]
      : PAY_ON_ARRIVAL_HOOK[parsedLang];
  const editorialDescription =
    truncateAtWord(stripHtml(a.overview), 160) ||
    a.subtitle ||
    `${a.title}, ${locationLabel}.`;
  const metaDescription = exploreHooks
    ? exploreHooks.meta(a.title, a.cityName)
    : isExplore
    ? editorialDescription
    : `${truncateAtWord(stripHtml(a.overview), 100) || a.subtitle || `${a.title}, ${locationLabel}.`} ${payHook}`.trim();
  const ogImage = a.images?.[0] ? absoluteImageUrl(a.images[0]) : undefined;
  const ogLocale = OG_LOCALE[parseLang(lang)];

  // Recently-viewed tracking — delegated to caller.
  const recentImage = a.images?.[0];
  useEffect(() => {
    if (a.mode === "explore") return;
    props.onRecentViewed?.({
      slug: a.slug,
      title: a.title,
      city: a.cityName,
      price: a.price,
      currency: "MAD",
      priceType: a.typePrice,
      image: recentImage,
      category: a.category?.name,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a.mode, a.slug, a.title, a.cityName, a.price, a.typePrice, recentImage, a.category?.name]);

  return (
    <BookingSkinScope skin={skin}>
    <main className="bg-[#faf9fd] md:pb-0">
      {/* If caller provides renderSeo, use that; otherwise emit built-in head tags */}
      {props.renderSeo
        ? props.renderSeo({ activity: a, lang })
        : (
          <>
            <title>{metaTitle}</title>
            <meta name="description" content={metaDescription} />
            <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1" />
            <meta property="og:title" content={metaTitle} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:type" content="product" />
            <meta property="og:site_name" content="MaJourneys" />
            <meta property="og:url" content={canonical} />
            <meta property="og:locale" content={ogLocale} />
            {SUPPORTED_LANGS
              .filter((l) => l !== lang)
              .map((l) => (
                <meta
                  key={`alt-${l}`}
                  property="og:locale:alternate"
                  content={OG_LOCALE[l]}
                />
              ))}
            {ogImage && <meta property="og:image" content={ogImage} />}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={metaTitle} />
            <meta name="twitter:description" content={metaDescription} />
            {ogImage && <meta name="twitter:image" content={ogImage} />}
            <meta name="geo.placename" content={locationLabel} />
            <meta name="geo.region" content="MA" />
            <meta name="geo.country" content={countryName} />
            {a.latitude && a.longitude && (
              <>
                <meta name="geo.position" content={`${a.latitude};${a.longitude}`} />
                <meta name="ICBM" content={`${a.latitude}, ${a.longitude}`} />
              </>
            )}
            <link rel="canonical" href={canonical} />
            {SUPPORTED_LANGS.map((l) => (
              <link
                key={`hl-${l}`}
                rel="alternate"
                hrefLang={l}
                href={`https://majourneys.com${altPath(l)}`}
              />
            ))}
            <link rel="alternate" hrefLang="x-default" href={`https://majourneys.com${altPath("en")}`} />
            {ldDocs.map((doc, i) => (
              <script
                // eslint-disable-next-line react/no-danger
                key={i}
                type="application/ld+json"
                // biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD is safe serialized data
                dangerouslySetInnerHTML={{ __html: JSON.stringify(doc) }}
              />
            ))}
          </>
        )
      }

      <Container className="pt-2 lg:pt-6">
        {/* The top of the page (lab concept 37): name with Share and Save,
            the facts line, the photos in one pewter frame.
            One tree for every screen size, so the page has exactly one <h1>
            and the server HTML carries the name, the facts and photo one. */}
        <ActivityPageTop
          slug={a.slug}
          title={a.title}
          subtitle={a.subtitle}
          cityName={a.cityName}
          categoryName={categoryLabel ?? null}
          images={a.images}
          facts={a.facts}
          price={a.price}
          priceType={a.typePrice}
          crumbs={visibleCrumbs}
          payment={topPaymentKind({
            slug: a.slug,
            isExplore,
            isStay: isStayFlow,
            isNightclub,
            isRestaurant: isTableFlow,
            bookingPaused: a.bookingPaused,
          })}
          wishlisted={showHeart ? wishlisted : undefined}
          onToggleWishlist={showHeart ? onHeart : undefined}
          renderShareModal={(shareProps) => <ShareModal {...shareProps} />}
          galleryRef={galleryRef}
          video={props.video}
          videoMeta={videoMeta}
          videoDescription={showsSubtitle(a.title, a.subtitle) ? a.subtitle : null}
          videoCta={videoCta}
          /* A guide is a route, not one pin (same rule as the Location card). */
          map={
            a.latitude && a.longitude && template !== "explore-guide"
              ? { latitude: a.latitude, longitude: a.longitude }
              : null
          }
          onMenu={
            hasMenu
              ? () => {
                  if (typeof document === "undefined") return;
                  document.getElementById("activity-menu")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              : undefined
          }
          titleAction={
            /* A restaurant has no package list to scroll to, so the way in
               sits next to the name. Table flow only. */
            isTableFlow && !a.bookingPaused ? (
              <button
                type="button"
                onClick={() => openBookingSheet()}
                className="inline-flex h-10 shrink-0 items-center gap-2 rounded-full bg-[#110E39] px-5 font-sans text-[14px] font-bold text-white transition hover:bg-[#241f52] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#110E39]"
              >
                <Icon name="ri-calendar-check-line" className="text-[15px]" aria-hidden="true" />
                {t("activity.book_table")}
              </button>
            ) : null
          }
        >
          {openingHours && <OpenNowPill hours={openingHours} className="mt-3" />}
          {womenTrip && (
            <WomenTripLine
              className="mt-2"
              organiserName={womenTrip.organiser?.name ?? null}
              organiserHref={
                womenTrip.organiser ? `/${lang}/organiser/${womenTrip.organiser.slug}${searchStr ?? ""}` : null
              }
            />
          )}
        </ActivityPageTop>
        <Toaster />

        {/* Optional inline slot (e.g. explore-wrapper neighbourhood + tips) */}
        {props.renderInlineSlot?.({ activity: a, lang })}

        <div
          /* `mt-3` on a phone, `lg:mt-8` unchanged: the mobile hero now ends
             in a hairline, so the old 24px on top of the hero's own padding
             read as a hole between the chips and the tabs. */
          className={
            isExplore
              ? "mt-3 grid grid-cols-1 gap-6 lg:mt-8"
              : "mt-3 grid grid-cols-1 gap-6 lg:mt-8 lg:grid-cols-[2fr_1fr] lg:gap-8"
          }
        >
          <div className="flex flex-col gap-5">
            {!isExplore && (
              <div className="hidden lg:block">
                <Suspense fallback={null}>
                  {/* A restaurant table is free (tables have no price), so
                      it never claims "pay later" (owner, 2026-10-13), and a
                      camp paid by link never claims "pay on the day". */}
                  <ActivityOptions
                    reservePayLater={a.reservePayLater && !isTableFlow && !paysByLink(a.slug) && !womenTrip}
                    freeCancellation={a.freeCancellation}
                    whatsappConfirmation={a.whatsappConfirmation}
                  />
                </Suspense>
              </div>
            )}

            {template === "explore-guide" && (
              <Suspense fallback={null}>
                <GuideItinerary stops={(contentBlocks as Record<string, unknown> | null)?.stops} lang={lang} />
              </Suspense>
            )}

            {/* A page with its "why people go" rows shows those instead of the
                restaurant fact tiles: the tiles say the same things, only
                shorter. */}
            {hasWhyGo ? (
              <WhyGoPanel blocks={contentBlocks as Record<string, unknown> | null} />
            ) : (
              isRestaurant && (
                /* The facts the top of the page already shows are left out,
                   so nothing is said twice. */
                <FactTiles facts={splitTopFacts(a).rest} />
              )
            )}

            <Suspense fallback={null}>
              <ActivityInfoTabs
                overviewHtml={a.overview}
                template={template}
                blocks={contentBlocks}
                hideItinerary={template === "explore-guide"}
                hideBlocks={hiddenBlocks}
              />
            </Suspense>

            <div id="activity-menu" className="scroll-mt-[96px]">
            <Suspense fallback={null}>
              <ActivityMenu
                blocks={contentBlocks}
                menuLink={a.menuLink}
                template={template}
                categorySlug={a.category?.slug}
              />
            </Suspense>
            </div>

            {/* Self-gates on the venue having a plan, so it renders for
                Léopard and nothing at all for everyone else. */}
            <Suspense fallback={null}>
              <ActivityFloorPlan slug={a.slug} venueName={a.title} />
            </Suspense>

            {!isExplore && !isTransfer && (
              <Suspense fallback={null}>
                <ActivityLineup activityId={a.id} onBook={(date) => openBookingSheet(undefined, { date })} />
              </Suspense>
            )}

            {isNightclub && (
              <Suspense fallback={null}>
                <NightclubFloorPlan
                  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                  tables={(a.packages as any[]).filter((p) => (p.maxGuests ?? 1) > 1)}
                  onBook={(packageId) => openBookingSheet(packageId)}
                />
              </Suspense>
            )}

            {!isExplore && a.category?.slug === "villa" && (
              <Suspense fallback={null}>
                <VillaPolicySection />
              </Suspense>
            )}

            {!isExplore && !isTransfer && a.packages.length > 0 && a.typePrice !== 2 && template !== "stay" && template !== "restaurant" && (
              <Suspense fallback={null}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <AvailablePacks
                  packs={a.packages as any}
                  bookHref={bookHref}
                  typePrice={a.typePrice as 0 | 1 | 2 | 3 | undefined}
                  categorySlug={a.category?.slug ?? null}
                  activitySlug={a.slug}
                  // A club and a women-only trip book in the sheet, never the /packages pages.
                  onBook={isNightclub || womenTrip ? (packageId) => openBookingSheet(packageId) : undefined}
                />
              </Suspense>
            )}

            {/* A guide is a route across a city, so a single pin says nothing —
                its stops each link to their own page and map instead. */}
            {isRestaurant ? (
              <LocationAndHours
                latitude={a.latitude}
                longitude={a.longitude}
                place={a.cityName}
                hours={openingHours ?? undefined}
              />
            ) : a.latitude && a.longitude && template !== "explore-guide" && (
              <LocationCard
                  latitude={a.latitude}
                  longitude={a.longitude}
                  place={a.cityName}
                  blocks={contentBlocks as Record<string, unknown> | null}
                  packages={a.packages}
                />
            )}

            {faqs.length > 0 && (
              <Suspense fallback={null}>
                <ActivityFaq faqs={faqs} />
              </Suspense>
            )}
          </div>

          {/* A club's box asks which night, not which time slot (see
              NightclubSidebar). Everything else keeps the standard sidebar. */}
          {isNightclub && (
            <div id="activity-booking-sidebar" className="scroll-mt-[88px]">
              <Suspense fallback={null}>
                <NightclubSidebar
                  activityId={a.id}
                  cityName={a.cityName}
                  // biome-ignore lint: narrow cast
                  packages={a.packages as any}
                  lang={lang}
                  onBook={(date) => openBookingSheet(undefined, date ? { date } : undefined)}
                  title={a.title}
                  thumbnail={a.images[0]}
                  wishlisted={showHeart ? wishlisted : undefined}
                  onToggleWishlist={showHeart ? onHeart : undefined}
                  renderShareModal={(shareProps) => <ShareModal {...shareProps} />}
                />
              </Suspense>
            </div>
          )}

          {isTransfer && (
            <div id="activity-booking-sidebar" className="scroll-mt-[88px]">
              {transferConfig ? (
                <TransferForm
                  lang={parsedLang}
                  routes={[{
                    id: a.id,
                    slug: a.slug,
                    title: a.title,
                    cityName: a.cityName,
                    categorySlug: a.category?.slug ?? null,
                    config: transferConfig,
                    packages: transferPackages,
                  }]}
                />
              ) : (
                <BookingSkinScope skin="pewter">
                  <div
                    style={{ ...pewterPanel(22), color: PW.navy }}
                    className="p-4 font-sans text-[14px]"
                    data-testid="transfer-unavailable"
                  >
                    <p className="mb-2">{t("transport.unavailable")}</p>
                    <a href={WHATSAPP_URL} className="inline-flex min-h-6 items-center font-semibold underline">
                      {t("transport.whatsapp")}
                    </a>
                  </div>
                </BookingSkinScope>
              )}
            </div>
          )}

          {!isExplore && !isNightclub && !isTransfer && (
            <div id="activity-booking-sidebar" className="scroll-mt-[88px]">
              <ActivitySidebar
                slug={a.slug}
                cityName={a.cityName}
                categorySlug={a.category?.slug ?? null}
                price={a.price}
                typePrice={a.typePrice as 0 | 1 | 2 | 3}
                thumbnail={a.images[0]}
                title={a.title}
                packagesCount={a.packages.length}
                // biome-ignore lint: narrow cast
                packages={a.packages as unknown as ActivitySidebarProps["packages"]}
                fixedTimeSlots={a.sessionTimes ?? null}
                tablePolicy={a.tablePolicy ?? null}
                openDays={a.openDays ?? null}
                minNoticeHours={a.minNoticeHours ?? null}
                openingHours={a.openingHours ?? null}
                lang={lang}
                freeCancellation={a.freeCancellation}
                infantMaxAge={a.infantMaxAge}
                childrenMaxAge={a.childrenMaxAge}
                minPeople={a.minPeople ?? 1}
                initialSelection={urlSelection}
                isTableFlow={isTableFlow}
                sheetOnly={isNightclub || Boolean(womenTrip)}
                womenTrip={womenTrip}
                onBookSheet={
                  isTableFlow || a.packages.length > 0
                    ? (selection) => openBookingSheet(undefined, selection)
                    : undefined
                }
                wishlisted={showHeart ? wishlisted : undefined}
                onToggleWishlist={showHeart ? onHeart : undefined}
                renderShareModal={(shareProps) => <ShareModal {...shareProps} />}
              />
            </div>
          )}
        </div>

        <Suspense fallback={null}>
          <SimilarListings
            activity={a}
            lang={lang}
            fallbackItems={props.similarItems}
            fallbackCityItems={props.similarCityItems}
          />
        </Suspense>

        {props.renderRelatedArticles?.({ activity: a, lang })}
      </Container>

      {!isExplore && (
        <BookingBar
          title={a.title}
          cityName={a.cityName}
          price={a.price}
          isTableFlow={isTableFlow}
          isStayFlow={isStayFlow}
          packageCount={a.packages.length}
          typePriceKey={PRICE_LABEL_KEYS[a.typePrice]}
          typePriceLabel={a.typePrice === 3 ? unitLabels.count(1) : undefined}
          bookHref={bookHref}
          thumbnail={a.images[0]}
          observeRef={galleryRef as React.RefObject<HTMLElement>}
          slug={a.slug}
          categorySlug={a.category?.slug ?? null}
          lang={lang}
          ctaLabel={womenTrip ? (tripFull ? t("women.join_waiting_list") : t("women.reserve_seat")) : undefined}
          onBookSheet={
            // A full trip's way in is the waiting list, which lives in the sidebar.
            isTransfer || tripFull
              ? () => {
                  if (typeof document === "undefined") return;
                  document.getElementById("activity-booking-sidebar")?.scrollIntoView({ behavior: "smooth" });
                }
              : isTableFlow || a.packages.length > 0
              ? () => openBookingSheet()
              : undefined
          }
        />
      )}

      {/* A club books by night, not by calendar slot: one screen for the night,
          one for table-or-ticket, one for details. */}
      {isNightclub && bookingSheetOpen && (
        <Suspense fallback={null}>
          <NightclubSheet
            open={bookingSheetOpen}
            onClose={() => setBookingSheetOpen(false)}
            activity={{
              id: a.id,
              slug: a.slug,
              title: a.title,
              cityName: a.cityName,
              thumbnail: a.images[0],
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              packages: a.packages as any,
              openDays: a.openDays ?? null,
            }}
            lang={lang}
            initialDate={initialSelection?.date}
            initialPeople={
              initialSelection?.adults ? initialSelection.adults + (initialSelection.children ?? 0) : undefined
            }
            initialPackageId={initialPkgId}
            sessionUser={props.sessionUser}
            ticketIconUrl="/images/subcategories/sub-festivals.png"
          />
        </Suspense>
      )}

      {!isNightclub && !isExplore && !isTransfer && (isTableFlow || a.packages.length > 0) && bookingSheetOpen && (
        <Suspense fallback={null}>
          <BookingSheet
            open={bookingSheetOpen}
            isSignedIn={props.isSignedIn}
            sessionUser={props.sessionUser}
            onClose={() => setBookingSheetOpen(false)}
            // biome-ignore lint: booking activity shape cast
            activity={({
              id: a.id,
              slug: a.slug,
              title: a.title,
              cityName: a.cityName,
              citySlug: a.city?.slug ?? null,
              latitude: toCoord(a.latitude),
              longitude: toCoord(a.longitude),
              categorySlug: a.category?.slug ?? null,
              template,
              typePrice: a.typePrice as 0 | 1 | 2 | 3,
              packages: a.packages.map((p) => ({
                ...p,
                timeStart: p.timeStart ?? undefined,
                timeEnd: p.timeEnd ?? undefined,
                typePrice: (p.typePrice ?? 0) as 0 | 1 | 2 | 3,
              })),
              freeCancellation: a.freeCancellation,
              thumbnail: a.images[0],
              infantMaxAge: a.infantMaxAge,
              childrenMaxAge: a.childrenMaxAge,
              minPeople: a.minPeople ?? 1,
              timeSlots: a.sessionTimes ?? null,
              openDays: a.openDays ?? null,
              minNoticeHours: a.minNoticeHours ?? null,
              openingHours: a.openingHours ?? null,
              tablePolicy: a.tablePolicy ?? null,
              bookingPaused: a.bookingPaused ?? false,
              bookingResumesOn: a.bookingResumesOn ?? null,
              bookingPausedNote: a.bookingPausedNote ?? null,
              contentBlocks,
              womenTrip,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }) as any}
            lang={lang}
            initialPkgId={initialPkgId}
            initialSelection={initialSelection}
            onAddedToBucket={(count) => setBucketToast({ count })}
          />
        </Suspense>
      )}

      {bucketToast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed inset-x-0 bottom-[calc(88px+env(safe-area-inset-bottom,0px))] z-[80] flex justify-center px-4 lg:bottom-8"
        >
          <div className="flex items-center gap-3 rounded-full border border-[rgba(4,0,51,0.08)] bg-white px-4 py-3 shadow-[0_18px_40px_rgba(4,0,51,0.18)]">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#16a34a]/15 text-[#16a34a]">
              <Icon name="ri-check-line" className="text-[16px]" aria-hidden />
            </span>
            <span className="font-sans text-[13px] font-semibold text-[#110E39]">
              {/* Real plural forms: the old "item{{plural}}" glued an English
                  "s" onto Arabic ("عنصرs"), 2026-11-04. */}
              {t("bucket.toast_added", {
                defaultValue: "Added to bucket, {{count}} items",
                count: bucketToast.count,
              })}
            </span>
            <a
              href={`/${lang}/bucket`}
              className="rounded-full bg-[linear-gradient(180deg,#2E2878_0%,#1A1454_50%,#110E39_100%)] shadow-[inset_0_1.5px_0_rgba(255,255,255,0.22),inset_0_-1.5px_0_rgba(0,0,0,0.25)] px-3.5 py-1.5 font-sans text-[12px] font-bold text-white"
            >
              {t("bucket.toast_view", { defaultValue: "View bucket" })}
            </a>
            <button
              type="button"
              onClick={() => setBucketToast(null)}
              aria-label={t("common.close", { defaultValue: "Close" })}
              className="flex h-7 w-7 items-center justify-center rounded-full text-[#5a5d77] hover:bg-[#f4f1ff]"
            >
              <Icon name="ri-close-line" className="text-[15px]" aria-hidden />
            </button>
          </div>
        </div>
      )}
    </main>
    </BookingSkinScope>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Helper utilities (all pure — no external deps)
// ──────────────────────────────────────────────────────────────────────────────

/** A router search object back to `?a=1&b=2`; nested values are left out. */
function queryStringOf(search: Record<string, unknown> | null | undefined): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search ?? {})) {
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function stripHtml(html: string | undefined | null): string {
  if (!html) return "";
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

/** True when a content block holds at least one non-empty string in any locale. */
function hasBlockList(blocks: Record<string, unknown> | null | undefined, key: string): boolean {
  const block = blocks?.[key];
  if (!block || typeof block !== "object") return false;
  return Object.values(block as Record<string, unknown>).some(
    (list) => Array.isArray(list) && list.some((it) => typeof it === "string" && it.trim().length > 0),
  );
}

function truncateAtWord(input: string, max: number): string {
  if (input.length <= max) return input;
  const slice = input.slice(0, max);
  const lastSpace = slice.lastIndexOf(" ");
  const cut = lastSpace > max * 0.6 ? slice.slice(0, lastSpace) : slice;
  return `${cut.trimEnd()}…`;
}

/**
 * Meta-description closer for bookable pages. Pay-on-arrival is the one
 * SERP hook upfront-payment competitors cannot copy — every bookable
 * snippet ends on it.
 */
const PAY_ON_ARRIVAL_HOOK: Record<(typeof SUPPORTED_LANGS)[number], string> = {
  en: "Reserve free, pay on arrival. WhatsApp support.",
  fr: "Réservation gratuite, paiement sur place. Support WhatsApp.",
  ar: "احجز مجاناً وادفع عند الوصول. دعم عبر واتساب.",
  es: "Reserva gratis, paga al llegar. Soporte por WhatsApp.",
  it: "Prenota gratis, paghi all'arrivo. Assistenza WhatsApp.",
  de: "Kostenlos reservieren, Zahlung vor Ort. WhatsApp-Support.",
  nl: "Reserveer gratis, betaal ter plaatse. WhatsApp-support.",
};

/**
 * Stays (the desert camps in this catalogue) are paid online in full, from a
 * secure payment link our team emails after the reservation (owner,
 * 2026-09-25). The old 40% deposit closer described villas that moved to LiteAPI.
 */
const STAY_PAID_ONLINE_HOOK: Record<(typeof SUPPORTED_LANGS)[number], string> = {
  en: "Paid online in full by secure payment link. WhatsApp support.",
  fr: "Paiement en ligne intégral par lien sécurisé. Support WhatsApp.",
  ar: "الدفع كاملاً عبر الإنترنت برابط آمن. دعم عبر واتساب.",
  es: "Pago en línea completo con enlace seguro. Soporte por WhatsApp.",
  it: "Pagamento online completo con link sicuro. Assistenza WhatsApp.",
  de: "Vollständig online bezahlt per sicherem Zahlungslink. WhatsApp-Support.",
  nl: "Volledig online betaald via beveiligde betaallink. WhatsApp-support.",
};

/**
 * SERP copy for explore pages, keyed by segment. GSC shows explore queries are
 * intent-heavy ("menu de fomo marrakech", "avis", "prix", "horaires") — the
 * title must promise the answer. Explore pages have no booking flow, so no
 * explore hook may say "book", "reserve" or "pay on arrival": a SERP promise
 * the page cannot keep. Booking titles live in BOOKABLE_SEO_HOOKS.
 */
type ExploreSeoHook = {
  title: string;
  /**
   * Title without the "& See the Menu" / "& See Prices" half, for pages that
   * do not show a menu or prices. A title must not promise what the page lacks.
   */
  titleShort?: string;
  meta: (name: string, city: string) => string;
};
/**
 * Explore-only restaurants and spas: listed for discovery, not bookable through
 * MaJourneys. Same shape as EXPLORE_SEO_HOOKS, minus any booking promise.
 */
const EXPLORE_LISTING_HOOKS: Record<
  "restaurants" | "spas",
  Record<(typeof SUPPORTED_LANGS)[number], ExploreSeoHook>
> = {
  restaurants: {
    en: { title: ": Menu, Photos & Tips", titleShort: ": Photos & Tips", meta: (n, c) => `${n} in ${c}: photos, opening hours and local tips, checked by our team in Morocco. WhatsApp support 24/7.` },
    fr: { title: " : menu, photos & conseils", titleShort: " : photos & conseils", meta: (n, c) => `${n} à ${c} : photos, horaires et conseils locaux, vérifiés par notre équipe au Maroc. Assistance WhatsApp 24/7.` },
    ar: { title: ": القائمة والصور والنصائح", titleShort: ": الصور والنصائح", meta: (n, c) => `${n} في ${c}: صور ومواعيد ونصائح محلية تحقق منها فريقنا في المغرب. دعم عبر واتساب 24/7.` },
    es: { title: ": menú, fotos y consejos", titleShort: ": fotos y consejos", meta: (n, c) => `${n} en ${c}: fotos, horarios y consejos locales, verificados por nuestro equipo en Marruecos. Asistencia por WhatsApp 24/7.` },
    it: { title: ": menù, foto e consigli", titleShort: ": foto e consigli", meta: (n, c) => `${n} a ${c}: foto, orari e consigli locali, verificati dal nostro team in Marocco. Assistenza WhatsApp 24/7.` },
    de: { title: ": Speisekarte, Fotos & Tipps", titleShort: ": Fotos & Tipps", meta: (n, c) => `${n} in ${c}: Fotos, Öffnungszeiten und lokale Tipps, geprüft von unserem Team in Marokko. WhatsApp-Support 24/7.` },
    nl: { title: ": menu, foto's & tips", titleShort: ": foto's & tips", meta: (n, c) => `${n} in ${c}: foto's, openingstijden en lokale tips, gecontroleerd door ons team in Marokko. WhatsApp-support 24/7.` },
  },
  spas: {
    en: { title: ": Prices, Photos & Tips", titleShort: ": Photos & Tips", meta: (n, c) => `${n} in ${c}: treatments, photos, opening hours and local tips, checked by our team in Morocco. WhatsApp support 24/7.` },
    fr: { title: " : prix, photos & conseils", titleShort: " : photos & conseils", meta: (n, c) => `${n} à ${c} : soins, photos, horaires et conseils locaux, vérifiés par notre équipe au Maroc. Assistance WhatsApp 24/7.` },
    ar: { title: ": الأسعار والصور والنصائح", titleShort: ": الصور والنصائح", meta: (n, c) => `${n} في ${c}: العلاجات والصور والمواعيد ونصائح محلية تحقق منها فريقنا في المغرب. دعم عبر واتساب 24/7.` },
    es: { title: ": precios, fotos y consejos", titleShort: ": fotos y consejos", meta: (n, c) => `${n} en ${c}: tratamientos, fotos, horarios y consejos locales, verificados por nuestro equipo en Marruecos. Asistencia por WhatsApp 24/7.` },
    it: { title: ": prezzi, foto e consigli", titleShort: ": foto e consigli", meta: (n, c) => `${n} a ${c}: trattamenti, foto, orari e consigli locali, verificati dal nostro team in Marocco. Assistenza WhatsApp 24/7.` },
    de: { title: ": Preise, Fotos & Tipps", titleShort: ": Fotos & Tipps", meta: (n, c) => `${n} in ${c}: Anwendungen, Fotos, Öffnungszeiten und lokale Tipps, geprüft von unserem Team in Marokko. WhatsApp-Support 24/7.` },
    nl: { title: ": prijzen, foto's & tips", titleShort: ": foto's & tips", meta: (n, c) => `${n} in ${c}: behandelingen, foto's, openingstijden en lokale tips, gecontroleerd door ons team in Marokko. WhatsApp-support 24/7.` },
  },
};
type ExploreHookKey = "restaurants" | "spas" | "locations" | "stays";
function exploreHookKey(segment: string | null | undefined): ExploreHookKey {
  if (segment === "restaurants" || segment === "spas" || segment === "stays") return segment;
  return "locations";
}
const EXPLORE_SEO_HOOKS: Record<
  ExploreHookKey,
  Record<(typeof SUPPORTED_LANGS)[number], ExploreSeoHook>
> = {
  restaurants: {
    en: {
      title: ": Menu, Photos & Location 2026",
      titleShort: ": Photos & Location 2026",
      meta: (n, c) =>
        `${n} in ${c}: why it is worth the visit, photos and exactly where it is, checked by our team in Morocco. WhatsApp support 24/7.`,
    },
    fr: {
      title: " : menu, photos & adresse 2026",
      titleShort: " : photos & adresse 2026",
      meta: (n, c) =>
        `${n} à ${c} : pourquoi y aller, photos et emplacement exact, vérifiés par notre équipe au Maroc. Support WhatsApp 24/7.`,
    },
    ar: {
      title: ": القائمة والصور والموقع 2026",
      titleShort: ": الصور والموقع 2026",
      meta: (n, c) =>
        `${n} في ${c}: لماذا تستحق الزيارة، صور وموقعه بالضبط، تحقق منها فريقنا في المغرب. دعم واتساب 24/7.`,
    },
    es: {
      title: ": menú, fotos y ubicación 2026",
      titleShort: ": fotos y ubicación 2026",
      meta: (n, c) =>
        `${n} en ${c}: por qué vale la pena, fotos y dónde está exactamente, verificado por nuestro equipo en Marruecos. WhatsApp 24/7.`,
    },
    it: {
      title: ": menù, foto e posizione 2026",
      titleShort: ": foto e posizione 2026",
      meta: (n, c) =>
        `${n} a ${c}: perché merita una visita, foto e dove si trova esattamente, verificato dal nostro team in Marocco. WhatsApp 24/7.`,
    },
    de: {
      title: ": Speisekarte, Fotos & Lage 2026",
      titleShort: ": Fotos & Lage 2026",
      meta: (n, c) =>
        `${n} in ${c}: warum sich ein Besuch lohnt, Fotos und die genaue Lage, geprüft von unserem Marokko-Team. WhatsApp 24/7.`,
    },
    nl: {
      title: ": menu, foto's & locatie 2026",
      titleShort: ": foto's & locatie 2026",
      meta: (n, c) =>
        `${n} in ${c}: waarom het een bezoek waard is, foto's en waar het precies ligt, gecontroleerd door ons team in Marokko. WhatsApp 24/7.`,
    },
  },
  spas: {
    en: {
      title: ": Treatments, Prices & Location 2026",
      titleShort: ": Photos & Location 2026",
      meta: (n, c) =>
        `${n} in ${c}: what sets it apart, photos and exactly where it is, checked by our team in Morocco. WhatsApp support 24/7.`,
    },
    fr: {
      title: " : soins, prix & adresse 2026",
      titleShort: " : photos & adresse 2026",
      meta: (n, c) =>
        `${n} à ${c} : ce qui le distingue, photos et emplacement exact, vérifiés par notre équipe au Maroc. Support WhatsApp 24/7.`,
    },
    ar: {
      title: ": العلاجات والأسعار والموقع 2026",
      titleShort: ": الصور والموقع 2026",
      meta: (n, c) =>
        `${n} في ${c}: ما يميّزه، صور وموقعه بالضبط، تحقق منها فريقنا في المغرب. دعم واتساب 24/7.`,
    },
    es: {
      title: ": tratamientos, precios y ubicación 2026",
      titleShort: ": fotos y ubicación 2026",
      meta: (n, c) =>
        `${n} en ${c}: qué lo hace diferente, fotos y dónde está exactamente, verificado por nuestro equipo en Marruecos. WhatsApp 24/7.`,
    },
    it: {
      title: ": trattamenti, prezzi e posizione 2026",
      titleShort: ": foto e posizione 2026",
      meta: (n, c) =>
        `${n} a ${c}: cosa lo rende diverso, foto e dove si trova esattamente, verificato dal nostro team in Marocco. WhatsApp 24/7.`,
    },
    de: {
      title: ": Anwendungen, Preise & Lage 2026",
      titleShort: ": Fotos & Lage 2026",
      meta: (n, c) =>
        `${n} in ${c}: was es besonders macht, Fotos und die genaue Lage, geprüft von unserem Marokko-Team. WhatsApp 24/7.`,
    },
    nl: {
      title: ": behandelingen, prijzen & locatie 2026",
      titleShort: ": foto's & locatie 2026",
      meta: (n, c) =>
        `${n} in ${c}: wat het bijzonder maakt, foto's en waar het precies ligt, gecontroleerd door ons team in Marokko. WhatsApp 24/7.`,
    },
  },
  locations: {
    en: {
      title: ": Photos, Access & Tips 2026",
      meta: (n, c) =>
        `${n} in ${c}: photos, opening hours, access and local tips, verified by our Morocco team. Plan your visit with WhatsApp support 24/7.`,
    },
    fr: {
      title: " : photos, accès & infos 2026",
      meta: (n, c) =>
        `${n} à ${c} : photos, horaires, accès et conseils locaux, vérifiés par notre équipe au Maroc. Support WhatsApp 24/7.`,
    },
    ar: {
      title: ": الصور والمواعيد والمعلومات 2026",
      meta: (n, c) =>
        `${n} في ${c}: صور، مواعيد، طرق الوصول ونصائح محلية، من فريقنا في المغرب. دعم واتساب 24/7.`,
    },
    es: {
      title: ": fotos, acceso y consejos 2026",
      meta: (n, c) =>
        `${n} en ${c}: fotos, horarios, acceso y consejos locales, verificados por nuestro equipo en Marruecos. WhatsApp 24/7.`,
    },
    it: {
      title: ": foto, accesso e consigli 2026",
      meta: (n, c) =>
        `${n} a ${c}: foto, orari, accesso e consigli locali, verificati dal nostro team in Marocco. WhatsApp 24/7.`,
    },
    de: {
      title: ": Fotos, Anfahrt & Tipps 2026",
      meta: (n, c) =>
        `${n} in ${c}: Fotos, Öffnungszeiten, Anfahrt und lokale Tipps, geprüft von unserem Marokko-Team. WhatsApp 24/7.`,
    },
    nl: {
      title: ": foto's, bereikbaarheid & tips 2026",
      meta: (n, c) =>
        `${n} in ${c}: foto's, openingstijden, bereikbaarheid en lokale tips, geverifieerd door ons team in Marokko. WhatsApp 24/7.`,
    },
  },
  /**
   * Explore-mode stays are EDITORIAL riad/hotel pages — we do not sell rooms in
   * them, so the copy must not promise a reservation.
   */
  stays: {
    en: {
      title: ": Rooms, Photos & Location 2026",
      meta: (n, c) =>
        `${n} in ${c}: the house, its rooms and amenities, and exactly where it sits, checked by our team in Morocco. WhatsApp support 24/7.`,
    },
    fr: {
      title: " : chambres, photos & emplacement 2026",
      meta: (n, c) =>
        `${n} à ${c} : la maison, ses chambres et équipements, et son emplacement exact, vérifiés par notre équipe au Maroc. Assistance WhatsApp 24/7.`,
    },
    ar: {
      title: ": الغرف والصور والموقع 2026",
      meta: (n, c) =>
        `${n} في ${c}: الرياض وغرفه ومرافقه وموقعه بالضبط، تحقق منها فريقنا في المغرب. خدمة واتساب 24/7.`,
    },
    es: {
      title: ": habitaciones, fotos y ubicación 2026",
      meta: (n, c) =>
        `${n} en ${c}: la casa, sus habitaciones y servicios, y dónde se encuentra exactamente, verificado por nuestro equipo en Marruecos. WhatsApp 24/7.`,
    },
    it: {
      title: ": camere, foto e posizione 2026",
      meta: (n, c) =>
        `${n} a ${c}: la casa, le sue camere e i servizi, e dove si trova esattamente, verificato dal nostro team in Marocco. WhatsApp 24/7.`,
    },
    de: {
      title: ": Zimmer, Fotos & Lage 2026",
      meta: (n, c) =>
        `${n} in ${c}: das Haus, seine Zimmer und Ausstattung und die genaue Lage, geprüft von unserem Marokko-Team. WhatsApp 24/7.`,
    },
    nl: {
      title: ": kamers, foto's & locatie 2026",
      meta: (n, c) =>
        `${n} in ${c}: het huis, de kamers en voorzieningen, en waar het precies ligt, gecontroleerd door ons team in Marokko. WhatsApp 24/7.`,
    },
  },
};

/**
 * Title hooks for BOOKABLE restaurants and spas only: the page has a booking
 * flow, so "Book a Table" is a promise it keeps. Their meta description ends on
 * PAY_ON_ARRIVAL_HOOK instead.
 */
const BOOKABLE_SEO_HOOKS: Record<
  "restaurants" | "spas",
  Record<(typeof SUPPORTED_LANGS)[number], Pick<ExploreSeoHook, "title" | "titleShort">>
> = {
  restaurants: {
    en: { title: ": Book a Table & See the Menu", titleShort: ": Book a Table" },
    fr: { title: " : réserver une table & voir le menu", titleShort: " : réserver une table" },
    ar: { title: ": احجز طاولة واطّلع على القائمة", titleShort: ": احجز طاولة" },
    es: { title: ": reservar mesa y ver el menú", titleShort: ": reservar mesa" },
    it: { title: ": prenota un tavolo e vedi il menù", titleShort: ": prenota un tavolo" },
    de: { title: ": Tisch reservieren & Speisekarte ansehen", titleShort: ": Tisch reservieren" },
    nl: { title: ": tafel reserveren & menu bekijken", titleShort: ": tafel reserveren" },
  },
  spas: {
    en: { title: ": Book a Treatment & See Prices", titleShort: ": Book a Treatment" },
    fr: { title: " : réserver un soin & voir les prix", titleShort: " : réserver un soin" },
    ar: { title: ": احجز علاجاً واطّلع على الأسعار", titleShort: ": احجز علاجاً" },
    es: { title: ": reservar tratamiento y ver precios", titleShort: ": reservar tratamiento" },
    it: { title: ": prenota un trattamento e vedi i prezzi", titleShort: ": prenota un trattamento" },
    de: { title: ": Anwendung buchen & Preise ansehen", titleShort: ": Anwendung buchen" },
    nl: { title: ": behandeling boeken & prijzen bekijken", titleShort: ": behandeling boeken" },
  },
};

type FaqSentenceTemplate = (subject: string, list: string) => string;
/** All 7 shipped locales — this was en/fr/ar, which emitted English block text
 * and English generated FAQs into the JSON-LD of es/it/de/nl pages. */
type Locale = "en" | "fr" | "ar" | "es" | "it" | "de" | "nl";
const JSONLD_LOCALES: readonly Locale[] = ["en", "fr", "ar", "es", "it", "de", "nl"];
type LocalizedText = Partial<Record<Locale, string>>;
type LocalizedList = Partial<Record<Locale, unknown>>;
type JsonLdItineraryStop = {
  title?: LocalizedText;
  description?: LocalizedText;
  durationMinutes?: number | null;
  tags?: string[];
  /** City-guide stops only: path of the venue's own page. */
  activityPath?: string | null;
};

const FAQ_QUESTION_TEMPLATES: Record<
  Locale,
  Record<string, { question: (subject: string) => string; answer: FaqSentenceTemplate }>
> = {
  en: {
    included: {
      question: (s) => `What is included in ${s}?`,
      answer: (s, list) => `${s} includes ${list}.`,
    },
    excluded: {
      question: (s) => `What is not included in ${s}?`,
      answer: (s, list) => `${s} does not include ${list}.`,
    },
    pickupInfo: {
      question: (s) => `Where is the pickup or meeting point for ${s}?`,
      answer: (_s, list) => `Pickup and meeting details: ${list}.`,
    },
    amenities: {
      question: (s) => `What amenities are available for ${s}?`,
      answer: (s, list) => `Amenities at ${s} include ${list}.`,
    },
    menuHighlights: {
      question: (s) => `What are the menu highlights at ${s}?`,
      answer: (s, list) => `Menu highlights at ${s} include ${list}.`,
    },
    scheduleInfo: {
      question: (s) => `What is the schedule for ${s}?`,
      answer: (_s, list) => `The schedule covers ${list}.`,
    },
    whatToBring: {
      question: (s) => `What should I bring for ${s}?`,
      answer: (s, list) => `For ${s} bring ${list}.`,
    },
    infoBeforeBooking: {
      question: (s) => `What should I know before booking ${s}?`,
      answer: (s, list) => `Before booking ${s}, note: ${list}.`,
    },
  },
  fr: {
    included: {
      question: (s) => `Qu'est-ce qui est inclus dans ${s} ?`,
      answer: (s, list) => `${s} inclut ${list}.`,
    },
    excluded: {
      question: (s) => `Qu'est-ce qui n'est pas inclus dans ${s} ?`,
      answer: (s, list) => `${s} n'inclut pas ${list}.`,
    },
    pickupInfo: {
      question: (s) => `Où se trouve le point de rendez-vous pour ${s} ?`,
      answer: (_s, list) => `Détails du point de rendez-vous : ${list}.`,
    },
    amenities: {
      question: (s) => `Quels équipements sont disponibles pour ${s} ?`,
      answer: (s, list) => `Les équipements à ${s} incluent ${list}.`,
    },
    menuHighlights: {
      question: (s) => `Quels sont les plats phares de ${s} ?`,
      answer: (s, list) => `Les plats phares de ${s} incluent ${list}.`,
    },
    scheduleInfo: {
      question: (s) => `Quel est le programme de ${s} ?`,
      answer: (_s, list) => `Le programme comprend ${list}.`,
    },
    whatToBring: {
      question: (s) => `Que dois-je apporter pour ${s} ?`,
      answer: (s, list) => `Pour ${s}, apportez ${list}.`,
    },
    infoBeforeBooking: {
      question: (s) => `Que faut-il savoir avant de réserver ${s} ?`,
      answer: (s, list) => `Avant de réserver ${s} : ${list}.`,
    },
  },
  ar: {
    included: {
      question: (s) => `ما الذي يتضمنه ${s}؟`,
      answer: (s, list) => `يتضمن ${s} ${list}.`,
    },
    excluded: {
      question: (s) => `ما الذي لا يتضمنه ${s}؟`,
      answer: (s, list) => `لا يتضمن ${s} ${list}.`,
    },
    pickupInfo: {
      question: (s) => `أين نقطة الانطلاق أو اللقاء لـ ${s}؟`,
      answer: (_s, list) => `تفاصيل نقطة اللقاء: ${list}.`,
    },
    amenities: {
      question: (s) => `ما المرافق المتوفرة في ${s}؟`,
      answer: (s, list) => `تشمل مرافق ${s}: ${list}.`,
    },
    menuHighlights: {
      question: (s) => `ما أبرز أطباق ${s}؟`,
      answer: (s, list) => `تشمل أبرز أطباق ${s}: ${list}.`,
    },
    scheduleInfo: {
      question: (s) => `ما هو برنامج ${s}؟`,
      answer: (_s, list) => `يشمل البرنامج: ${list}.`,
    },
    whatToBring: {
      question: (s) => `ماذا يجب أن أحضر لـ ${s}؟`,
      answer: (s, list) => `لـ ${s} أحضر: ${list}.`,
    },
    infoBeforeBooking: {
      question: (s) => `ما الذي يجب معرفته قبل حجز ${s}؟`,
      answer: (s, list) => `قبل حجز ${s}: ${list}.`,
    },
  },
  es: {
    included: {
      question: (s) => `¿Qué incluye ${s}?`,
      answer: (s, list) => `${s} incluye ${list}.`,
    },
    excluded: {
      question: (s) => `¿Qué no incluye ${s}?`,
      answer: (s, list) => `${s} no incluye ${list}.`,
    },
    pickupInfo: {
      question: (s) => `¿Dónde es el punto de encuentro de ${s}?`,
      answer: (_s, list) => `Detalles del punto de encuentro: ${list}.`,
    },
    amenities: {
      question: (s) => `¿Qué servicios tiene ${s}?`,
      answer: (s, list) => `Los servicios de ${s} incluyen ${list}.`,
    },
    menuHighlights: {
      question: (s) => `¿Cuáles son los platos destacados de ${s}?`,
      answer: (s, list) => `Los platos destacados de ${s} incluyen ${list}.`,
    },
    scheduleInfo: {
      question: (s) => `¿Cuál es el programa de ${s}?`,
      answer: (_s, list) => `El programa incluye ${list}.`,
    },
    whatToBring: {
      question: (s) => `¿Qué hay que llevar a ${s}?`,
      answer: (s, list) => `Para ${s} conviene llevar ${list}.`,
    },
    infoBeforeBooking: {
      question: (s) => `¿Qué conviene saber antes de reservar ${s}?`,
      answer: (s, list) => `Antes de reservar ${s}, tenga en cuenta: ${list}.`,
    },
  },
  it: {
    included: {
      question: (s) => `Che cosa include ${s}?`,
      answer: (s, list) => `${s} include ${list}.`,
    },
    excluded: {
      question: (s) => `Che cosa non include ${s}?`,
      answer: (s, list) => `${s} non include ${list}.`,
    },
    pickupInfo: {
      question: (s) => `Dov'è il punto d'incontro per ${s}?`,
      answer: (_s, list) => `Dettagli del punto d'incontro: ${list}.`,
    },
    amenities: {
      question: (s) => `Quali servizi offre ${s}?`,
      answer: (s, list) => `I servizi di ${s} includono ${list}.`,
    },
    menuHighlights: {
      question: (s) => `Quali sono i piatti forti di ${s}?`,
      answer: (s, list) => `I piatti forti di ${s} includono ${list}.`,
    },
    scheduleInfo: {
      question: (s) => `Qual è il programma di ${s}?`,
      answer: (_s, list) => `Il programma comprende ${list}.`,
    },
    whatToBring: {
      question: (s) => `Che cosa portare per ${s}?`,
      answer: (s, list) => `Per ${s} conviene portare ${list}.`,
    },
    infoBeforeBooking: {
      question: (s) => `Che cosa sapere prima di prenotare ${s}?`,
      answer: (s, list) => `Prima di prenotare ${s}, nota: ${list}.`,
    },
  },
  de: {
    included: {
      question: (s) => `Was ist in ${s} enthalten?`,
      answer: (s, list) => `${s} umfasst ${list}.`,
    },
    excluded: {
      question: (s) => `Was ist in ${s} nicht enthalten?`,
      answer: (s, list) => `${s} umfasst nicht ${list}.`,
    },
    pickupInfo: {
      question: (s) => `Wo ist der Treffpunkt für ${s}?`,
      answer: (_s, list) => `Angaben zum Treffpunkt: ${list}.`,
    },
    amenities: {
      question: (s) => `Welche Ausstattung bietet ${s}?`,
      answer: (s, list) => `Zur Ausstattung von ${s} gehören ${list}.`,
    },
    menuHighlights: {
      question: (s) => `Was sind die Highlights der Karte von ${s}?`,
      answer: (s, list) => `Zu den Highlights von ${s} gehören ${list}.`,
    },
    scheduleInfo: {
      question: (s) => `Wie sieht der Ablauf von ${s} aus?`,
      answer: (_s, list) => `Der Ablauf umfasst ${list}.`,
    },
    whatToBring: {
      question: (s) => `Was sollte man zu ${s} mitbringen?`,
      answer: (s, list) => `Für ${s} mitbringen: ${list}.`,
    },
    infoBeforeBooking: {
      question: (s) => `Was sollte man vor der Buchung von ${s} wissen?`,
      answer: (s, list) => `Vor der Buchung von ${s} beachten: ${list}.`,
    },
  },
  nl: {
    included: {
      question: (s) => `Wat is inbegrepen bij ${s}?`,
      answer: (s, list) => `${s} omvat ${list}.`,
    },
    excluded: {
      question: (s) => `Wat is niet inbegrepen bij ${s}?`,
      answer: (s, list) => `${s} omvat geen ${list}.`,
    },
    pickupInfo: {
      question: (s) => `Waar is het ontmoetingspunt voor ${s}?`,
      answer: (_s, list) => `Details van het ontmoetingspunt: ${list}.`,
    },
    amenities: {
      question: (s) => `Welke voorzieningen heeft ${s}?`,
      answer: (s, list) => `De voorzieningen van ${s} zijn onder meer ${list}.`,
    },
    menuHighlights: {
      question: (s) => `Wat zijn de aanraders op de kaart bij ${s}?`,
      answer: (s, list) => `De aanraders van ${s} zijn onder meer ${list}.`,
    },
    scheduleInfo: {
      question: (s) => `Wat is het programma van ${s}?`,
      answer: (_s, list) => `Het programma omvat ${list}.`,
    },
    whatToBring: {
      question: (s) => `Wat moet je meenemen naar ${s}?`,
      answer: (s, list) => `Neem voor ${s} ${list} mee.`,
    },
    infoBeforeBooking: {
      question: (s) => `Wat is goed om te weten voordat je ${s} boekt?`,
      answer: (s, list) => `Let op voordat je ${s} boekt: ${list}.`,
    },
  },
};

function buildContentBlockFaqs(
  title: string,
  blocks: Record<string, unknown> | null | undefined,
  lang: string,
): Array<{ id: string; question: string; answer: string }> {
  if (!blocks) return [];
  const locale = pickJsonLdLocale(lang);
  const templates = FAQ_QUESTION_TEMPLATES[locale];
  const keys: Array<{ id: string; key: keyof typeof templates }> = [
    { id: "block-included", key: "included" },
    { id: "block-excluded", key: "excluded" },
    { id: "block-pickup", key: "pickupInfo" },
    { id: "block-amenities", key: "amenities" },
    { id: "block-menu", key: "menuHighlights" },
    { id: "block-schedule", key: "scheduleInfo" },
    { id: "block-bring", key: "whatToBring" },
    { id: "block-before-booking", key: "infoBeforeBooking" },
  ];
  const out: Array<{ id: string; question: string; answer: string }> = [];
  for (const { id, key } of keys) {
    const raw = readJsonLdList(blocks, String(key), locale).slice(0, 6);
    // Menu bullets are authored as "Course: dish"; the course label is a
    // heading on the Menu card, not part of the dish, so drop it here or the
    // answer reads "include Starter: tuna tataki, Starter: foie gras".
    const items = key === "menuHighlights" ? raw.map((it) => splitCourse(it).dish) : raw;
    if (items.length === 0) continue;
    const tmpl = templates[String(key)];
    if (!tmpl) continue;
    out.push({
      id,
      question: tmpl.question(title),
      answer: blockFaqAnswer(tmpl.answer, title, items, locale),
    });
  }
  return out;
}

type JsonLdActivity = {
  slug: string;
  title: string;
  subtitle?: string | null;
  overview?: string | null;
  cityName: string;
  city: { slug: string };
  category: { name: string; slug: string } | null;
  images: string[];
  price: number;
  typePrice: number;
  rating?: number | string | null;
  reviewsCount?: number | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  hideLocation?: boolean;
  freeCancellation?: boolean;
  reservePayLater?: boolean;
  template?: string | null;
  contentBlocks?: Record<string, unknown> | null;
  menuLink?: string | null;
  openingHours?: unknown;
  mode?: "bookable" | "explore";
  segment?: string | null;
};

type JsonLdCrumb = { label: string; href?: string };
type JsonLdFaq = { question: string; answer: string };
type JsonLdTransferOffer = { name: string; price: number; maxGuests: number };

/** Where MaJourneys' own images and the Organization node live. */
const SITE_ORIGIN = "https://majourneys.com";

/**
 * The same TravelAgency the root layout and the home page describe in full,
 * referenced by @id so every node on the site points at one organization.
 */
const ORGANIZATION_REF = {
  "@type": "TravelAgency",
  "@id": `${SITE_ORIGIN}/#organization`,
  name: "MaJourneys",
  url: SITE_ORIGIN,
} as const;

/**
 * Hero for a transfer route with no photo of its own: the fleet, instead of
 * the generic landscape the API sends when a row has no images.
 */
const TRANSFER_FALLBACK_IMAGE = "/images/transport/fleet-hero.webp";

function withTransferFallbackImage(a: ShowActivity): ShowActivity {
  if (a.mode === "explore" || a.template !== "transfer") return a;
  const real = (a.images ?? []).filter((u) => u && u !== ACTIVITY_IMAGE_PLACEHOLDER);
  return real.length > 0 ? a : { ...a, images: [TRANSFER_FALLBACK_IMAGE] };
}

/** og:image and JSON-LD need absolute URLs; a site-relative path gets the site origin. */
function absoluteImageUrl(url: string): string {
  return url.startsWith("/") && !url.startsWith("//") ? `${SITE_ORIGIN}${url}` : url;
}

function buildActivityJsonLd(args: {
  activity: JsonLdActivity;
  lang: string;
  crumbs: JsonLdCrumb[];
  faqs: JsonLdFaq[];
  canonical: string;
  /** Priced vehicles of a transfer route; null for every other template. */
  transferOffers?: JsonLdTransferOffer[] | null;
}): object[] {
  const { activity: a, lang, crumbs, faqs, canonical, transferOffers } = args;
  const description =
    stripHtml(a.overview).slice(0, 500) ||
    a.subtitle ||
    `${a.title} in ${formatMoroccoLocation(a.cityName, lang)}.`;
  const lat = typeof a.latitude === "string" ? Number.parseFloat(a.latitude) : a.latitude ?? null;
  const lng = typeof a.longitude === "string" ? Number.parseFloat(a.longitude) : a.longitude ?? null;
  const ratingNum = typeof a.rating === "string" ? Number.parseFloat(a.rating) : a.rating ?? null;
  const inLanguage = bcp47Lang(lang);
  const isExplore = a.mode === "explore";
  // Bookable rows used to all read as TouristAttraction, so search engines
  // and AI answers never saw "you can book this here". Type them by category
  // and give each a ReserveAction pointing at our page.
  const bookableType = isExplore ? null : bookableSchemaType(a.template, a.category?.slug);
  const primaryType = isExplore ? exploreSchemaType(a.segment) : bookableType!.type;

  const attraction: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": primaryType,
    "@id": `${canonical}#attraction`,
    name: a.title,
    description,
    image: (a.images ?? []).slice(0, 8).map(absoluteImageUrl),
    url: canonical,
    inLanguage,
    isPartOf: { "@id": "https://majourneys.com/#website" },
    provider: { "@id": "https://majourneys.com/#organization" },
    address: {
      "@type": "PostalAddress",
      addressLocality: a.cityName,
      addressRegion: a.cityName,
      addressCountry: "MA",
    },
  };

  if (bookableType?.type === "TouristAttraction" && a.category?.name) {
    attraction.touristType = a.category.name;
  }

  if (bookableType) {
    if (bookableType.type !== "TouristTrip") {
      attraction.acceptsReservations = true;
    }
    attraction.potentialAction = {
      "@type": "ReserveAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: canonical,
        inLanguage,
        actionPlatform: [
          "https://schema.org/DesktopWebPlatform",
          "https://schema.org/MobileWebPlatform",
        ],
      },
      result: { "@type": bookableType.reservation, name: a.title },
      provider: { "@id": "https://majourneys.com/#organization" },
    };
  }

  if (primaryType === "Article") {
    attraction.author = {
      "@type": "Organization",
      "@id": "https://majourneys.com/#organization",
      name: "MaJourneys",
      url: "https://majourneys.com",
    };
    attraction.publisher = { "@id": "https://majourneys.com/#organization" };
    attraction.headline = a.title;
  }

  // A venue that hides its location (the guest gets it after booking) keeps
  // its coordinates out of the markup too.
  if (!a.hideLocation && lat !== null && lng !== null && Number.isFinite(lat) && Number.isFinite(lng)) {
    attraction.geo = {
      "@type": "GeoCoordinates",
      latitude: lat,
      longitude: lng,
    };
  }

  // Restaurants, bookable or listed: the facts AI answers and Google's local
  // results ask for first. Only what the row actually holds; no cuisine,
  // street address or price range, because none has a structured source.
  if (primaryType === "Restaurant") {
    const hours = openingHoursToSchemaOrg(a.openingHours);
    if (hours.length > 0) attraction.openingHoursSpecification = hours;
    // Same rule as the Menu block's "View the full menu" button: the link the
    // API already resolved for this page's language.
    const menu = typeof a.menuLink === "string" ? a.menuLink.trim() : "";
    if (/^https?:\/\//i.test(menu)) attraction.hasMenu = menu;
  }

  // Star ratings only on stays (owner rule): every other page hides them, so
  // its markup must not claim them either.
  if (
    !isExplore &&
    a.template === "stay" &&
    Number.isFinite(ratingNum) &&
    ratingNum! > 0 &&
    a.reviewsCount &&
    a.reviewsCount > 1
  ) {
    attraction.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: ratingNum,
      bestRating: 5,
      worstRating: 1,
      reviewCount: a.reviewsCount,
    };
  }

  if (!isExplore) {
    // No price for a restaurant table, even from a stale package row: Google
    // must not show "From X MAD" for something free to reserve (owner rule).
    const restaurant = isRestaurantListing({
      mode: a.mode,
      segment: a.segment,
      template: a.template,
      categorySlug: a.category?.slug,
    });
    const priceNum =
      !restaurant && typeof a.price === "number" && Number.isFinite(a.price) && a.price > 0 ? a.price : null;
    const validThrough = (() => {
      // Morocco's day, not the host's. This string is rendered into the JSON-LD
      // script during SSR, so a server on UTC and a browser an hour ahead used
      // to emit different dates and fail hydration along with the date strip.
      const d = moroccoStartOfToday();
      d.setDate(d.getDate() + 30);
      return toLocalISO(d);
    })();
    attraction.offers = {
      "@type": "Offer",
      "@id": `${canonical}#offer`,
      url: canonical,
      availability: "https://schema.org/InStock",
      businessFunction: "https://schema.org/Reserve",
      seller: { "@id": "https://majourneys.com/#organization" },
      ...(priceNum !== null
        ? {
            price: priceNum,
            priceCurrency: "MAD",
            priceValidUntil: validThrough,
          }
        : {}),
    };
  }

  // A transfer is a ride, not a place to visit: one Service node with an
  // Offer per priced vehicle replaces the TouristAttraction and its Offer.
  const isTransfer = !isExplore && a.template === "transfer";
  const heroImage = a.images?.[0] ? absoluteImageUrl(a.images[0]) : null;
  const primary: Record<string, unknown> = isTransfer
    ? {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${canonical}#service`,
        serviceType: "Private transfer",
        name: a.title,
        description,
        url: canonical,
        inLanguage,
        ...(heroImage ? { image: heroImage } : {}),
        areaServed: { "@type": "City", name: a.cityName },
        provider: ORGANIZATION_REF,
        offers: (transferOffers ?? []).map((o) => ({
          "@type": "Offer",
          name: o.name,
          price: o.price,
          priceCurrency: "MAD",
          eligibleQuantity: {
            "@type": "QuantitativeValue",
            maxValue: o.maxGuests,
            unitText: "people",
          },
          availability: "https://schema.org/InStock",
          url: canonical,
        })),
      }
    : attraction;

  const docs: object[] = [primary];
  const blockDocs = buildContentBlockJsonLd({ activity: a, lang, canonical });
  docs.push(...blockDocs);

  if (faqs.length > 0) {
    docs.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${canonical}#faq`,
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    });
  }

  // Google already prints the domain as the first hop of the SERP trail, so a
  // "Home" crumb renders as `majourneys.com › Home › Marrakech › Spas`. Drop it
  // from the markup only — the visible breadcrumb keeps the link for humans.
  // Positions renumber from the sliced list; Google invalidates trails that
  // skip a position.
  const ldCrumbs = crumbs[0]?.href === `/${lang}` ? crumbs.slice(1) : crumbs;

  docs.push({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${canonical}#breadcrumb`,
    itemListElement: ldCrumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.label,
      ...(c.href ? { item: `https://majourneys.com${c.href}` } : {}),
    })),
  });

  return docs;
}

function buildContentBlockJsonLd(args: {
  activity: JsonLdActivity;
  lang: string;
  canonical: string;
}): object[] {
  const { activity, lang, canonical } = args;
  const locale = pickJsonLdLocale(lang);
  const blocks = activity.contentBlocks ?? {};
  const docs: object[] = [];

  // `itinerary` on trip/event templates, `stops` on city guides — same shape.
  const itinerary = [
    ...readJsonLdItinerary(blocks, "itinerary"),
    ...readJsonLdItinerary(blocks, "stops"),
  ];
  if (itinerary.length > 0) {
    docs.push({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${canonical}#itinerary`,
      name: `${activity.title} itinerary`,
      description: `Day-by-day itinerary for ${activity.title} in ${getMoroccoName(lang)}.`,
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      numberOfItems: itinerary.length,
      itemListElement: itinerary.map((stop, index) => {
        const name = pickJsonLdText(stop.title, locale) || `Stop ${index + 1}`;
        const description = pickJsonLdText(stop.description, locale);
        // Guide stops carry the path of the venue's own page — emitting it
        // gives the list real URLs for search and AI answers to follow.
        let stopUrl: string | null = null;
        if (stop.activityPath) {
          try {
            stopUrl = new URL(`/${lang}/${stop.activityPath}`, canonical).toString();
          } catch {
            stopUrl = null;
          }
        }
        return {
          "@type": "ListItem",
          position: index + 1,
          name,
          ...(stopUrl ? { url: stopUrl } : {}),
          item: {
            "@type": "TouristAttraction",
            name,
            ...(stopUrl ? { url: stopUrl } : {}),
            ...(description ? { description } : {}),
            ...(stop.tags && stop.tags.length > 0 ? { keywords: stop.tags.join(", ") } : {}),
          },
        };
      }),
    });
  }

  const listBlocks = [
    { key: "highlights", id: "highlights", name: `${activity.title} highlights` },
  ];

  for (const block of listBlocks) {
    const items = readJsonLdList(blocks, block.key, locale);
    if (items.length === 0) continue;
    docs.push({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${canonical}#${block.id}`,
      name: block.name,
      itemListElement: items.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item,
      })),
    });
  }

  return docs;
}

function pickJsonLdLocale(lang: string): Locale {
  const base = (lang ?? "en").split("-")[0] as Locale;
  return JSONLD_LOCALES.includes(base) ? base : "en";
}

function pickJsonLdText(value: LocalizedText | undefined, locale: Locale): string {
  if (!value) return "";
  return value[locale]?.trim() || value.en?.trim() || "";
}

function readJsonLdList(blocks: Record<string, unknown>, key: string, locale: Locale): string[] {
  const block = blocks[key] as LocalizedList | undefined;
  if (!block) return [];
  const raw = block[locale] ?? block.en;
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
}

function readJsonLdItinerary(blocks: Record<string, unknown>, key: string): JsonLdItineraryStop[] {
  const raw = blocks[key];
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (item): item is JsonLdItineraryStop => typeof item === "object" && item !== null,
  );
}

function bcp47Lang(lang: string): string {
  if (lang === "fr") return "fr-MA";
  if (lang === "ar") return "ar-MA";
  return "en-US";
}

function bookableSchemaType(
  template: string | null | undefined,
  categorySlug: string | null | undefined,
): { type: string; reservation: string } {
  if (template === "restaurant") {
    return { type: "Restaurant", reservation: "FoodEstablishmentReservation" };
  }
  if (template === "stay") return { type: "LodgingBusiness", reservation: "LodgingReservation" };
  if (template === "trip") return { type: "TouristTrip", reservation: "Reservation" };
  switch (categorySlug) {
    case "spas":
      return { type: "DaySpa", reservation: "Reservation" };
    case "nightclubs":
      return { type: "NightClub", reservation: "Reservation" };
    default:
      return { type: "TouristAttraction", reservation: "Reservation" };
  }
}

function exploreSchemaType(segment: string | null | undefined): string {
  switch (segment) {
    case "restaurants":
      return "Restaurant";
    case "spas":
      return "HealthAndBeautyBusiness";
    case "stays":
      return "LodgingBusiness";
    case "guides":
      return "Article";
    case "locations":
    default:
      return "TouristAttraction";
  }
}

type FaqActivity = {
  slug: string;
  title: string;
  cityName: string;
  category: { name: string } | null;
  reservePayLater?: boolean;
  freeCancellation?: boolean;
  whatsappConfirmation?: boolean;
  womenTrip?: WomenTripInfo | null;
};

function buildDefaultFaqs(a: FaqActivity, t: TFunction) {
  const faqs: { id: string; question: string; answer: string }[] = [];

  faqs.push({
    id: "default-booking",
    question: t("activity.faq_default.how_to_book_q", {
      title: a.title,
      defaultValue: "How do I book {{title}}?",
    }),
    answer: a.whatsappConfirmation
      ? t("activity.faq_default.how_to_book_a_whatsapp", {
          defaultValue:
            "Pick your date on this page, choose a pack and number of guests, then submit your reservation. Our team will confirm with the provider and notify you on WhatsApp.",
        })
      : t("activity.faq_default.how_to_book_a_email", {
          defaultValue:
            "Pick your date on this page, choose a pack and number of guests, then submit your reservation. Our team will confirm with the provider and notify you by email.",
        }),
  });

  faqs.push({
    id: "default-payment",
    question: t("activity.faq_default.payment_q", {
      defaultValue: "Do I pay online?",
    }),
    answer: a.womenTrip
      ? t("women.faq_payment_a")
      : paysByLink(a.slug)
      ? t("activity.faq_default.payment_a_link")
      : t("activity.faq_default.payment_a", {
          defaultValue:
            "No, you reserve online for free and pay the local provider on arrival. The provider will tell you which payment methods they accept on the day.",
        }),
  });

  if (a.freeCancellation) {
    faqs.push({
      id: "default-cancellation",
      question: t("activity.faq_default.cancellation_q", {
        defaultValue: "Can I cancel my reservation?",
      }),
      answer: t("activity.faq_default.cancellation_a", {
        defaultValue:
          "Yes. This experience offers free cancellation up to 24 hours before the start time, message our team on WhatsApp and we'll cancel for you, no questions asked.",
      }),
    });
  }

  faqs.push({
    id: "default-meeting",
    question: t("activity.faq_default.meeting_q", {
      title: a.title,
      defaultValue: "Where does {{title}} take place?",
    }),
    answer: t("activity.faq_default.meeting_a", {
      city: a.cityName,
      defaultValue:
        "This experience takes place in {{city}}. Exact meeting point and pickup details are shared right after your booking is confirmed.",
    }),
  });

  faqs.push({
    id: "default-duration",
    question: t("activity.faq_default.duration_q", {
      defaultValue: "How long does the experience last?",
    }),
    answer: t("activity.faq_default.duration_a", {
      defaultValue:
        "Duration depends on the pack you choose, you'll find the start and end time on each pack card on this page.",
    }),
  });

  faqs.push({
    id: "default-whatsapp",
    question: t("activity.faq_default.support_q", {
      defaultValue: "What if I need help?",
    }),
    answer: t("activity.faq_default.support_a", {
      defaultValue:
        "Our team is available 7 days a week on WhatsApp, tap the green Need Help card on this page and a local agent will reply within minutes.",
    }),
  });

  return faqs;
}

function ShowSkeleton() {
  return (
    <div className="flex items-center justify-center py-32">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

export function ActivityShowPage(props: ActivityShowPageProps) {
  return (
    <Suspense fallback={<ShowSkeleton />}>
      <ShowContent {...props} />
    </Suspense>
  );
}

/** A coordinate the API may send as a string ("31.63"); null when absent or not a number. */
function toCoord(v: number | string | null | undefined): number | null {
  const n = typeof v === "string" ? Number.parseFloat(v) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}
